import type { HttpClient } from '../http/HttpClient'
import type {
  CurrencyTotal,
  RequestGroupAction,
  RequestGroupActionKey,
  RequestGroupActionResult,
  RequestGroupActionRow,
  RequestGroupAssociations,
  RequestGroupCreation,
  RequestGroupInvoiceType,
  RequestGroupMemberStatus,
  RequestGroupPurchaseOrderStatus,
  RequestGroupRepository,
  RequestGroupSummary,
} from '@/domain/requestGroup/RequestGroup'
import type { PurchaseOrderProgressState } from '@/domain/purchaseOrder/PurchaseOrderLifecycle'
import { REQUEST_GROUP_ACTIONS } from '@/domain/requestGroup/RequestGroup'
import type { GeneratePurchaseOrdersRow, GroupReceptionInput, GroupReceptionRow } from '@/domain/requestGroup/RequestGroupOrders'
import type { GroupAwardRequest, GroupComparisonRead, GroupComparisonReason } from '@/domain/requestGroup/GroupComparisonRead'
import type { GroupMemberResult, GroupMemberStatus, GroupQuotationRequest } from '@/domain/requestGroup/GroupQuotation'
import type { GroupInvoiceResult, GroupInvoicesAssociation } from '@/domain/requestGroup/groupAssociations'
import { toAssociationPreviewResult, type RawAssociationPreviewResult } from '../invoice/associationPreviewResult'

interface RawRequestGroupCreation {
  request_group: string | null
  requests: { id: number; society_id: number; request_number: number }[]
}

interface RawNamedRef { id: number; name: string }

interface RawMemberSummary {
  request_id: number
  request_number: number | null
  society: RawNamedRef
  status: RequestGroupMemberStatus
  current_step: { node_id: number; type: string; name: string } | null
  pending_approvers: { id: number; name: string; avatar: string | null }[]
  totals_by_currency: CurrencyTotal[]
  awarded_by_currency: CurrencyTotal[]
  in_purchase_orders_by_currency: CurrencyTotal[]
  budget: { amount: number | null; currency: string | null; coverage: 'COVERED' | 'NOT_COVERED' | 'PENDING' | null }
  available_actions: Partial<Record<RequestGroupActionKey, RequestGroupAction>>
}

interface RawSummary {
  request_group: string
  request_group_number?: number | null
  members: RawMemberSummary[]
  totals_by_currency: CurrencyTotal[]
  awarded_by_currency: CurrencyTotal[]
  in_purchase_orders_by_currency: CurrencyTotal[]
}

interface RawMemberAssociations {
  request: { id: number; request_number: number | null; status: RequestGroupMemberStatus; society: RawNamedRef & { country: string | null } }
  purchase_orders: {
    id: number; number: string | null; internal_number: number | null; status: RequestGroupPurchaseOrderStatus; provider: RawNamedRef
    currency: string | null; total: number; net_total: number; awarded: number | null; received_total: number
    invoiced_total: number; paid_total: number; pending_reception: number; pending_invoicing: number
    reception_state: PurchaseOrderProgressState; invoicing_state: PurchaseOrderProgressState; reception_ids: number[]; invoice_ids: number[]; payment_ids: number[]
    can_receive?: boolean
  }[]
  awarded_by_currency: CurrencyTotal[]
  awarded_without_purchase_order: { provider: RawNamedRef | null; currency: string | null; awarded: number }[]
  receptions: { id: number; number: number; date: string; purchase_order_id: number | null; amount: number }[]
  invoices: {
    id: number; number: string | null; tax_authority_uid: string | null; cufe: string | null; type: RequestGroupInvoiceType; status: string
    provider_id: number; purchase_order_id: number | null; reception_ids: number[]; currency: string | null
    total: number | null; payment_summary: { status: string | null; paid: number; scheduled: number }
    credit_notes: { id: number; number: string | null; status: string; currency: string | null; total: number | null }[]
  }[]
  invoices_without_purchase_order: number[]
  payments: {
    id: number; number: number | null; status: string; currency: string | null; total: number
    programmed_payment_date: string | null; payment_date: string | null
    invoice_links: { invoice_id: number; amount: number }[]; purchase_order_ids: number[]
  }[]
  society_mismatches: { type: 'purchase_order' | 'reception' | 'invoice' | 'credit_note' | 'payment'; id: number; society_id: number }[]
}

interface RawAssociations {
  request_group: string
  members: RawMemberAssociations[]
  totals_by_currency: {
    currency: string | null; awarded: number; in_purchase_orders: number; received: number; invoiced: number; paid: number
  }[]
}

const DISABLED: RequestGroupAction = { enabled: false, reason: null }

function actions(raw: RawMemberSummary['available_actions']): Record<RequestGroupActionKey, RequestGroupAction> {
  return Object.fromEntries(
    REQUEST_GROUP_ACTIONS.map((key) => [key, raw?.[key] ?? DISABLED]),
  ) as Record<RequestGroupActionKey, RequestGroupAction>
}

interface RawGroupComparison {
  request_group: string
  available: boolean
  reason: GroupComparisonReason | null
  partial?: boolean
  partial_reason?: GroupComparisonRead['partialReason']
  currency: string | null
  members: {
    request_id: number
    request_number: number | string | null
    society: RawNamedRef
    available: boolean
    reason: string | null
    currency: string | null
  }[]
  variables: GroupComparisonRead['variables']
  providers: GroupComparisonRead['providers']
  items: GroupComparisonRead['items']
  totals: GroupComparisonRead['totals']
  best_provider_ids: number[]
  best_combination_total: number | null
  awarded_total: number | null
}

function groupPath(groupId: string): string {
  return `purchases/request-groups/${encodeURIComponent(groupId)}`
}

interface RawMemberResults {
  results: {
    request_id: number | null
    society_id: number | null
    status: GroupMemberStatus
    reason: string | null
    detail?: string | null
    derived_request?: { id: number; request_number: number } | null
  }[]
}

function toMemberResults(raw: RawMemberResults): GroupMemberResult[] {
  return raw.results.map((row) => ({
    requestId: row.request_id ?? 0,
    societyId: row.society_id,
    status: row.status,
    reason: row.reason,
    detail: row.detail ?? null,
    derivedRequest: row.derived_request ? { id: row.derived_request.id, requestNumber: row.derived_request.request_number } : null,
  }))
}

interface RawRequestGroupActionResult {
  results: {
    request_id: number
    society_id: number | null
    status: RequestGroupActionRow['status']
    reason: string | null
    detail?: string | null
  }[]
}

export class HttpRequestGroupRepository implements RequestGroupRepository {
  constructor(private http: HttpClient) {}

  async create(form: FormData, idempotencyKey: string): Promise<RequestGroupCreation> {
    const raw = await this.http
      .url('purchases/request-groups')
      .header('Idempotency-Key', idempotencyKey)
      .body(form)
      .post<RawRequestGroupCreation>()

    return {
      requestGroup: raw.request_group,
      requests: raw.requests.map((member) => ({
        id: member.id,
        societyId: member.society_id,
        requestNumber: member.request_number,
      })),
    }
  }

  async getSummary(groupId: string): Promise<RequestGroupSummary> {
    const raw = await this.http.url(groupPath(groupId)).get<RawSummary>()
    return {
      requestGroup: raw.request_group,
      requestGroupNumber: raw.request_group_number ?? null,
      members: raw.members.map((member) => ({
        requestId: member.request_id,
        requestNumber: member.request_number,
        society: member.society,
        status: member.status,
        currentStep: member.current_step
          ? { nodeId: member.current_step.node_id, type: member.current_step.type, name: member.current_step.name }
          : null,
        pendingApprovers: member.pending_approvers,
        totalsByCurrency: member.totals_by_currency,
        awardedByCurrency: member.awarded_by_currency,
        inPurchaseOrdersByCurrency: member.in_purchase_orders_by_currency,
        budget: member.budget,
        availableActions: actions(member.available_actions),
      })),
      totalsByCurrency: raw.totals_by_currency,
      awardedByCurrency: raw.awarded_by_currency,
      inPurchaseOrdersByCurrency: raw.in_purchase_orders_by_currency,
    }
  }

  async getAssociations(groupId: string): Promise<RequestGroupAssociations> {
    const raw = await this.http.url(`${groupPath(groupId)}/associations`).get<RawAssociations>()
    return {
      requestGroup: raw.request_group,
      members: raw.members.map((member) => ({
        request: {
          id: member.request.id,
          requestNumber: member.request.request_number,
          status: member.request.status,
          society: member.request.society,
        },
        purchaseOrders: member.purchase_orders.map((po) => ({
          id: po.id,
          number: po.number,
          internalNumber: po.internal_number,
          status: po.status,
          provider: po.provider,
          currency: po.currency,
          total: po.total,
          netTotal: po.net_total,
          awarded: po.awarded,
          receivedTotal: po.received_total,
          invoicedTotal: po.invoiced_total,
          paidTotal: po.paid_total,
          pendingReception: po.pending_reception,
          pendingInvoicing: po.pending_invoicing,
          receptionState: po.reception_state,
          invoicingState: po.invoicing_state,
          receptionIds: po.reception_ids,
          invoiceIds: po.invoice_ids,
          paymentIds: po.payment_ids,
          canReceive: po.can_receive ?? false,
        })),
        awardedByCurrency: member.awarded_by_currency,
        awardedWithoutPurchaseOrder: member.awarded_without_purchase_order,
        receptions: member.receptions.map((reception) => ({
          id: reception.id,
          number: reception.number,
          date: reception.date,
          purchaseOrderId: reception.purchase_order_id,
          amount: reception.amount,
        })),
        invoices: member.invoices.map((invoice) => ({
          id: invoice.id,
          number: invoice.number,
          taxAuthorityUid: invoice.tax_authority_uid,
          cufe: invoice.cufe,
          type: invoice.type,
          status: invoice.status,
          providerId: invoice.provider_id,
          purchaseOrderId: invoice.purchase_order_id,
          receptionIds: invoice.reception_ids,
          currency: invoice.currency,
          total: invoice.total,
          paymentSummary: invoice.payment_summary,
          creditNotes: invoice.credit_notes,
        })),
        invoicesWithoutPurchaseOrder: member.invoices_without_purchase_order,
        payments: member.payments.map((payment) => ({
          id: payment.id,
          number: payment.number,
          status: payment.status,
          currency: payment.currency,
          total: payment.total,
          programmedPaymentDate: payment.programmed_payment_date,
          paymentDate: payment.payment_date,
          invoiceLinks: payment.invoice_links.map((link) => ({ invoiceId: link.invoice_id, amount: link.amount })),
          purchaseOrderIds: payment.purchase_order_ids,
        })),
        societyMismatches: member.society_mismatches.map((mismatch) => ({
          type: mismatch.type,
          id: mismatch.id,
          societyId: mismatch.society_id,
        })),
      })),
      totalsByCurrency: raw.totals_by_currency.map((row) => ({
        currency: row.currency,
        awarded: row.awarded,
        inPurchaseOrders: row.in_purchase_orders,
        received: row.received,
        invoiced: row.invoiced,
        paid: row.paid,
      })),
    }
  }

  approve(group: string, memberIds: number[], comments = ''): Promise<RequestGroupActionResult> {
    return this.act(group, 'approve', { member_ids: memberIds, comments })
  }

  cancel(group: string, memberIds: number[], reason: string): Promise<RequestGroupActionResult> {
    return this.act(group, 'cancel', { member_ids: memberIds, comments: reason })
  }

  async getGroupComparison(groupId: string): Promise<GroupComparisonRead> {
    const raw = await this.http.url(`${groupPath(groupId)}/comparison`).get<RawGroupComparison>()
    return {
      groupId: raw.request_group ?? groupId,
      available: raw.available,
      reason: raw.reason,
      partial: raw.partial === true,
      partialReason: raw.partial_reason ?? null,
      currency: raw.currency,
      members: raw.members.map((member) => ({
        requestId: member.request_id,
        requestNumber: member.request_number,
        society: member.society,
        available: member.available,
        reason: member.reason,
        currency: member.currency,
      })),
      variables: raw.variables,
      providers: raw.providers,
      items: raw.items,
      totals: raw.totals,
      best_provider_ids: raw.best_provider_ids,
      best_combination_total: raw.best_combination_total,
      awarded_total: raw.awarded_total,
    }
  }

  async awardGroup(groupId: string, request: GroupAwardRequest): Promise<GroupMemberResult[]> {
    const payload = {
      picks: request.picks.map((pick) => ({ product_line_id: pick.productLineId, quotation_line_id: pick.quotationLineId })),
      atomic: request.atomic,
      comments: request.comments,
    }
    let body: object | FormData = payload
    if (request.technicalReport) {
      const form = new FormData()
      form.append('payload', JSON.stringify(payload))
      form.append('technical_report', request.technicalReport)
      body = form
    }
    const raw = await this.http
      .url(`${groupPath(groupId)}/selection`)
      .body(body)
      .post<RawMemberResults>()
    return toMemberResults(raw)
  }

  async requestQuotations(group: string, request: GroupQuotationRequest): Promise<GroupMemberResult[]> {
    const emails = request.providerEmails ?? {}
    const created = new Map((request.newProviders ?? []).map((provider) => [provider.id, provider]))
    // why: a new provider (negative id) goes with the data the api creates it from; one the buyer did not check is never sent.
    const entry = <T extends { id: number }>(provider: T) => {
      const fresh = created.get(provider.id)
      return fresh ? { ...fresh, ...provider } : { ...provider, ...(emails[provider.id] ? { email: emails[provider.id] } : {}) }
    }
    const once = <T extends { id: number }>(providers: T[]): T[] => providers.filter((provider, index) => providers.findIndex((other) => other.id === provider.id) === index)
    const payload = {
      member_ids: request.memberIds,
      providers: once(request.providerIds.map((id) => ({ id }))).map(entry),
      members: request.lines.map((member) => ({
        request_id: member.requestId,
        items: member.items.map(({ files: _files, ...item }) => ({
          ...item,
          ...(item.providers ? { providers: once(item.providers).map(entry) } : {}),
        })),
      })),
      quotation_type: request.quotationType,
      expiry_date: request.expiryDate,
      send_form: request.sendForm ?? false,
    }
    const files = request.lines.flatMap((member) =>
      member.items.flatMap((item) => (item.files ?? []).map((file) => ({ key: `line_${member.requestId}_${item.id}_docs`, file }))),
    )
    let body: object | FormData = payload
    if (files.length) {
      const form = new FormData()
      form.append('payload', JSON.stringify(payload))
      files.forEach(({ key, file }) => form.append(key, file))
      body = form
    }
    const raw = await this.http.url(`${groupPath(group)}/quotations`).body(body).post<RawMemberResults>()
    return toMemberResults(raw)
  }

  async closeOffers(group: string, memberIds: number[], comments: string): Promise<GroupMemberResult[]> {
    const raw = await this.http
      .url(`${groupPath(group)}/close-offers`)
      .body({ member_ids: memberIds, comments })
      .post<RawMemberResults>()
    return toMemberResults(raw)
  }

  async associateInvoices(groupId: string, body: GroupInvoicesAssociation): Promise<GroupInvoiceResult[]> {
    const response = await this.http
      .url(`${groupPath(groupId)}/invoices/associate`)
      .body({
        invoices: body.invoices.map((invoice) => ({
          invoice_id: invoice.invoiceId,
          ...(invoice.amount !== undefined ? { amount: invoice.amount } : {}),
          ...(invoice.lineMapping ? { line_mappings: invoice.lineMapping } : {}),
        })),
        justification: body.justification ?? '',
        confirm_reassociation: body.confirmReassociation,
      })
      .post<{ results?: RawAssociationPreviewResult[] } | null>()
    return (response?.results ?? []).map(toAssociationPreviewResult)
  }

  private async act(group: string, action: 'approve' | 'cancel', body: object): Promise<RequestGroupActionResult> {
    const raw = await this.http
      .url(`${groupPath(group)}/${encodeURIComponent(action)}`)
      .body(body)
      .post<RawRequestGroupActionResult>()

    return {
      results: raw.results.map((row) => ({
        requestId: row.request_id,
        societyId: row.society_id,
        status: row.status,
        reason: row.reason,
        detail: row.detail ?? null,
      })),
    }
  }

  async generatePurchaseOrders(group: string, memberIds: number[]): Promise<GeneratePurchaseOrdersRow[]> {
    const raw = await this.http
      .url(`${groupPath(group)}/purchase-orders/generate`)
      .body({ member_ids: memberIds })
      .post<{ results: { request_id: number; society_id: number | null; status: GeneratePurchaseOrdersRow['status']; reason: string | null; detail?: string | null }[] }>()
    return raw.results.map((row) => ({
      requestId: row.request_id,
      societyId: row.society_id,
      status: row.status,
      reason: row.reason,
      detail: row.detail ?? null,
    }))
  }

  async receivePurchaseOrders(group: string, date: Date, receptions: GroupReceptionInput[]): Promise<GroupReceptionRow[]> {
    const payload = {
      date: date.toISOString(),
      receptions: receptions.map((reception) => ({
        purchase_order_id: reception.purchaseOrderId,
        reception_type: reception.receptionType,
        date: reception.date ? reception.date.toISOString() : null,
        description: reception.description,
        qualification: reception.qualification,
        criteria_scores: reception.criteriaScores,
        custom_fields_data: reception.customFieldsData,
        lines: reception.lines.map((line) => ({
          purchase_order_line: line.purchaseOrderLineId,
          quantity: line.quantity,
          net_total: line.netTotal,
          description: line.description,
          start_date: line.startDate ? line.startDate.toISOString() : null,
          end_date: line.endDate ? line.endDate.toISOString() : null,
        })),
      })),
    }
    const hasFiles = receptions.some((reception) => reception.files.length > 0)
    let body: object | FormData = payload
    if (hasFiles) {
      const form = new FormData()
      form.append('payload', JSON.stringify(payload))
      receptions.forEach((reception) =>
        reception.files.forEach((file) => form.append(`support_documents_${reception.purchaseOrderId}`, file)),
      )
      body = form
    }
    const raw = await this.http
      .url(`${groupPath(group)}/receptions`)
      .body(body)
      .post<{
        results: {
          request_id: number
          society_id: number | null
          status: GroupReceptionRow['status']
          reason: string | null
          detail?: string | null
          purchase_order_id?: number | null
          reception_id?: number | null
          reception_number?: number | string | null
        }[]
      }>()
    return raw.results.map((row) => ({
      requestId: row.request_id,
      societyId: row.society_id,
      status: row.status,
      reason: row.reason,
      detail: row.detail ?? null,
      purchaseOrderId: row.purchase_order_id ?? null,
      receptionId: row.reception_id ?? null,
      receptionNumber: row.reception_number ?? null,
    }))
  }
}
